import { useCallback } from "react";
import apiClient from "./apiClient";
import useApiWrapper from "./apiWrapper";
import { getDropdownBranchQuery } from "../utils/branchSession";

/** --------------------------
 *  Build Query Params
 *  ------------------------- */
const isDropdownRequest = (filters = {}) =>
    filters.drop_down === "True" ||
    filters.drop_down === true ||
    filters.dropdown === true;

const buildUrl = (baseUrl, filters = {}) => {
  const merged = { ...filters };
  if (isDropdownRequest(merged)) {
    const branchQuery = getDropdownBranchQuery();
    if (!merged.branch && branchQuery.branch) {
      Object.assign(merged, branchQuery);
    }
  }
  const queryParams = new URLSearchParams();

  Object.entries(merged).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      // Handle boolean flag (dropdown → true)
      queryParams.append(
        key === "dropdown" ? "drop_down" : key,
        key === "dropdown" ? true : value
      );
    }
  });

  return queryParams.toString()
    ? `${baseUrl}?${queryParams.toString()}`
    : baseUrl;
};

/** --------------------------
 *  useApiMethods Hook
 *  ------------------------- */
const useApiMethods = () => {
  const { apiWrapper } = useApiWrapper();

  const request = useCallback((method, url, data = null, options = {}, setLoading) => {
    return apiWrapper(() =>
      apiClient.request({
        method,
        url,
        data,
        ...options,
      }),
      setLoading
    );
  }, [apiWrapper]);

  const fetch = useCallback((url, filters = {}, setLoading, signal) => {
    const URL = buildUrl(`/${url}`, filters);
    return request("get", URL, null, signal ? { signal } : {}, setLoading);
  }, [request]);

  const create = useCallback((url, data, isFormData = false, setLoading, extraOptions) => {
    const headers = {
      "Content-Type": isFormData
        ? "multipart/form-data"
        : "application/json",
    };
    const options = { headers };
    if (extraOptions && typeof extraOptions === "object" && !Array.isArray(extraOptions)) {
      Object.assign(options, extraOptions);
    }
    return request("post", url, data, options, setLoading);
  }, [request]);

  const update = useCallback((url, data, isFormData = false, setLoading) => {
    const headers = {
      "Content-Type": isFormData
        ? "multipart/form-data"
        : "application/json",
    };
    return request("put", url, data, { headers }, setLoading);
  }, [request]);

  const remove = useCallback((url, data, setLoading) => {
    return request("delete", url, data, {}, setLoading);
  }, [request]);

  return { fetch, create, update, remove };
};

export default useApiMethods;
